import {
    aggregateTimeReportItems,
    formatDate,
    formatNumber,
    formatRange,
    formatWeekDayShort,
    getRange,
    GetTimeEntriesParams,
    GetTimeReportsParams,
    type TimeEntry,
    TimeEntryType,
    type TimeReport,
    toDurationString,
} from "@pentacode/core";
import type { DateString } from "@pentacode/openapi";
import { activeIcon } from "@pentacode/ui/components";
import { css, html, LitElement, nothing, type TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { app } from "../../init";
import { Routing } from "../../mixins/routing";
import { StateMixin } from "../../mixins/state";
import { config, shared } from "../../styles";
import { alert } from "../alert-dialog";
import "../date-range-picker";
import "../month-picker";
import "../scroller";

@customElement("ptc-staff-time")
export class StaffTime extends Routing(StateMixin(LitElement)) {
    readonly routePattern = /home|time/;

    @property({ reflect: true, type: Boolean })
    expanded = false;

    @property({ attribute: false })
    date: DateString;

    @state()
    private _loading = false;

    @state()
    private _timeEntriesByDate: {
        date: DateString;
        timeEntries: TimeEntry[];
    }[] = [];

    @state()
    private _timeReport: TimeReport | null = null;

    private get _dateRange() {
        return getRange(this.date, "month");
    }

    updated(changes: Map<string, unknown>) {
        if (changes.has("date")) {
            void this._load();
        }
    }

    static styles = [
        config,
        shared,
        css`
            :host {
                display: block;
                position: relative;
                background: var(--color-bg);
                margin-top: -0.5em;
            }

            .header {
                margin-left: 2em;
                border-left: 1px solid var(--shade-2);
                position: relative;
            }

            .month-header {
                margin-left: -1.1em;
            }

            .month-icon {
                border: solid 1px;
                border-color: var(--shade-2);
                padding: 0.2em;
                background: var(--color-bg);
                border-radius: 50%;
            }

            .shift-time {
                background: var(--color-bg);
                white-space: nowrap;
            }

            .day {
                margin-left: 2em;
                border-left: 1px solid var(--shade-2);
                padding: 0.5em;
                position: relative;
            }

            .day-icon {
                border: solid 1px;
                border-color: var(--shade-2);
                padding: 0.2em;
                background: var(--color-bg);
            }

            .day-header {
                margin-left: -1.55em;
            }

            .timeline-wrapper {
                padding: 0.5em 5em 0.5em 2em;
                height: 2em;
            }

            .shift-icon {
                border: solid 1px;
                padding: 0.2em;
                background: var(--color-bg);
            }

            .position-header {
                color: var(--color-highlight);
                position: relative;
                margin-left: -2.2em;
            }
        `,
    ];

    async refresh() {
        await this._load();
    }

    private async _load() {
        if (!app.profile || !this._dateRange) {
            return;
        }

        const { from, to } = this._dateRange;
        if (!from || !to) {
            return;
        }

        this._loading = true;

        try {
            const [timeEntries, timeReports] = await Promise.all([
                app.getTimeEntries(
                    new GetTimeEntriesParams({
                        from,
                        to,
                        employee: [app.profile.id],
                    })
                ),
                app.api.getTimeReports(
                    new GetTimeReportsParams({
                        from,
                        to,
                        filters: [{ type: "employeeId", value: app.profile.id }],
                        resolution: "month",
                        types: [
                            TimeEntryType.Work,
                            TimeEntryType.Vacation,
                            TimeEntryType.Sick,
                            TimeEntryType.SickInKUG,
                            TimeEntryType.ChildSick,
                            TimeEntryType.HourAdjustment,
                            TimeEntryType.CompDay,
                        ],
                    })
                ),
            ]);

            const timeEntriesByDate = new Map<DateString, TimeEntry[]>();

            for (const timeEntry of timeEntries) {
                if (
                    !timeEntry.result?.base.duration ||
                    ![
                        TimeEntryType.Work,
                        TimeEntryType.Vacation,
                        TimeEntryType.Sick,
                        TimeEntryType.SickInKUG,
                        TimeEntryType.ChildSick,
                        TimeEntryType.HourAdjustment,
                        TimeEntryType.CompDay,
                    ].includes(timeEntry.type)
                ) {
                    continue;
                }

                if (!timeEntriesByDate.has(timeEntry.date)) {
                    timeEntriesByDate.set(timeEntry.date, []);
                }

                timeEntriesByDate.get(timeEntry.date)!.push(timeEntry);
            }

            this._timeEntriesByDate = [...timeEntriesByDate.entries()].map(([date, timeEntries]) => ({
                date,
                timeEntries,
            }));
            this._timeReport = timeReports[0];
        } catch (e) {
            await alert(e.message, { type: "warning" });
        }

        this._loading = false;
    }

    private _formatTime(date: Date) {
        return `${date.getHours().toString().padStart(2, "0")}:${date.getMinutes().toString().padStart(2, "0")}`;
    }

    private _renderDay(
        { date, timeEntries }: (typeof this._timeEntriesByDate)[number],
        minTime: number,
        maxTime: number
    ) {
        const timeRange = maxTime - minTime;
        return html`
            <div class="day">
                <div class="center-aligning horizontal layout day-header">
                    <ptc-ui-icon name="icon.object.day-view" class="day-icon"></ptc-ui-icon>
                    <div class="padded subtle">${formatWeekDayShort(date)}, <strong>${formatDate(date)}</strong></div>
                </div>
                <div class="smaller padded spacing vertical layout">
                    ${timeEntries.map((timeEntry) => {
                        if (!timeEntry.result) {
                            return;
                        }
                        const startPosition =
                            timeEntry.startFinal &&
                            ((new Date(timeEntry.startFinal).getTime() - new Date(timeEntry.date).getTime() - minTime) /
                                timeRange) *
                                100;
                        const endPosition =
                            timeEntry.endFinal &&
                            ((new Date(timeEntry.endFinal).getTime() - new Date(timeEntry.date).getTime() - minTime) /
                                timeRange) *
                                100;
                        const typeIcon = app.localized.timeEntryTypeIcon(timeEntry.type);
                        return html`
                            <div class="time-entry">
                                <div class="spacing center-aligning horizontal layout">
                                    <div
                                        class="center-aligning horizontal layout position-header"
                                        style="--color-highlight: ${app.getTimeEntryColor(timeEntry)}"
                                    >
                                        <ptc-ui-icon
                                            name=${typeIcon ? activeIcon(typeIcon, true) : nothing}
                                            class="shift-icon"
                                        ></ptc-ui-icon>
                                        <div class="padded">${app.getTimeEntryLabel(timeEntry)}</div>
                                    </div>
                                    <div class="stretch ellipsis">
                                        ${timeEntry.comment
                                            ? html`<ptc-ui-icon name="icon.object.comment"></ptc-ui-icon>
                                                  ${timeEntry.comment}`
                                            : ""}
                                    </div>
                                    ${timeEntry.result.meals.breakfast
                                        ? html`
                                              <div class="brown semibold colored-text">
                                                  <ptc-ui-icon name="icon.object.meal-breakfast"></ptc-ui-icon>
                                              </div>
                                          `
                                        : ""}
                                    ${timeEntry.result.meals.lunch
                                        ? html`
                                              <div class="brown semibold colored-text">
                                                  <ptc-ui-icon name="icon.object.meal-lunch"></ptc-ui-icon>
                                              </div>
                                          `
                                        : ""}
                                    ${timeEntry.result.meals.dinner
                                        ? html`
                                              <div class="brown semibold colored-text">
                                                  <ptc-ui-icon name="icon.object.meal-dinner"></ptc-ui-icon>
                                              </div>
                                          `
                                        : ""}
                                    ${timeEntry.result.breaks.duration
                                        ? html`
                                              <div class="orange semibold colored-text">
                                                  <ptc-ui-icon name="icon.object.break"></ptc-ui-icon>
                                                  ${toDurationString(timeEntry.result.breaks.duration)}
                                              </div>
                                          `
                                        : ""}
                                    <div class="blue semibold colored-text">
                                        <ptc-ui-icon name="icon.object.duration"></ptc-ui-icon> ${toDurationString(
                                            timeEntry.result.base.duration
                                        )}
                                    </div>
                                </div>
                                ${timeEntry.startFinal && timeEntry.endFinal && startPosition && endPosition
                                    ? html`
                                          <div class="timeline-wrapper">
                                              <div class="relative">
                                                  <div
                                                      style="position: absolute; height: 2em; left: ${startPosition}%; width: ${endPosition -
                                                      startPosition}%;"
                                                  >
                                                      <div
                                                          style="position: absolute; inset: 0; margin: auto; height: 1px; background: ${app.getTimeEntryColor(
                                                              timeEntry
                                                          )}"
                                                      ></div>
                                                      <div
                                                          class="pill shift-time absolute"
                                                          style="left: 0; transform: translateX(-100%);"
                                                      >
                                                          <ptc-ui-icon name="icon.planning.shift-start"></ptc-ui-icon>
                                                          ${this._formatTime(timeEntry.startFinal)}
                                                      </div>
                                                      <div
                                                          class="pill shift-time absolute"
                                                          style="right: 0; transform: translateX(100%);"
                                                      >
                                                          <ptc-ui-icon name="icon.planning.shift-end"></ptc-ui-icon>
                                                          ${this._formatTime(timeEntry.endFinal)}
                                                      </div>
                                                  </div>
                                              </div>
                                          </div>
                                      `
                                    : ""}
                            </div>
                        `;
                    })}
                </div>
            </div>
        `;
    }

    render(): TemplateResult {
        const workTimeEntries = this._timeEntriesByDate
            .flatMap(({ timeEntries }) => timeEntries)
            .filter((timeEntry) => timeEntry.startFinal && timeEntry.endFinal);
        const minTime = 0;
        // The default time range is from 00:00 to 24:00 - if there are any shifts that reach into the next
        // day, we'll extend the time window up accordingly
        const maxTime = Math.max(
            ...workTimeEntries.map(
                (timeEntry) => new Date(timeEntry.endFinal!).getTime() - new Date(timeEntry.date).getTime()
            ),
            24 * 60 * 60 * 1000
        );

        const breakdown = aggregateTimeReportItems(this._timeReport?.items || []);

        const renderData = [
            {
                title: "Arbeit",
                color: "brown",
                icon: app.localized.timeEntryTypeIcon(TimeEntryType.Work),
                item: breakdown.work,
            },
            {
                title: "Urlaub",
                color: "blue",
                icon: app.localized.timeEntryTypeIcon(TimeEntryType.Vacation),
                item: breakdown.vacation,
            },
            {
                title: "Krank",
                color: "orange",
                icon: app.localized.timeEntryTypeIcon(TimeEntryType.Sick),
                item: breakdown.sick,
            },
            {
                title: "Guttag",
                color: "aqua",
                icon: app.localized.timeEntryTypeIcon(TimeEntryType.CompDay),
                item: breakdown.comp,
            },
            {
                title: "Buchungen",
                color: "black",
                icon: "icon.time-entry.adjustment",
                item: breakdown.adjustments,
            },
        ];

        return html`
            <div class="fullbleed vertical layout" style="background: var(--color-bg); z-index: 1;">
                <div class="header">
                    <div class="larger semibold center-aligning horizontal layout month-header">
                        <ptc-ui-icon name="icon.object.date" class="month-icon"></ptc-ui-icon>
                        <div class="horizontally-padded">${formatRange(this._dateRange)}</div>
                    </div>
                    <div class="horizontal scroller">
                        <div
                            class="double-spacing horizontal layout"
                            style="padding: 0.5em 1em 1em 1em; min-width: max-content;"
                        >
                            ${renderData.map(
                                (data) =>
                                    html`<div>
                                        <div class="${data.color} colored-text bottom-margined">
                                            <ptc-ui-icon name="${data.icon}"></ptc-ui-icon> ${data.title}
                                        </div>
                                        <div class="half-spacing center-aligning horizontal layout">
                                            <ptc-ui-icon name="icon.object.working-hours" class="smaller"></ptc-ui-icon>
                                            <div>${toDurationString(data.item.hours)}</div>
                                            <ptc-ui-icon name="icon.object.day-view" class="smaller"></ptc-ui-icon>
                                            <div>${formatNumber(data.item.days, 2)}</div>
                                        </div>
                                    </div>`
                            )}
                        </div>
                    </div>
                </div>

                <ptc-scroller class="stretch">
                    ${this._timeEntriesByDate.map((item) => this._renderDay(item, minTime, maxTime))}
                    ${this._timeEntriesByDate.length === 0
                        ? html`
                              <div class="double-padded fullbleed centering layout subtle">
                                  Es liegen keine Arbeitszeiten in diesem Zeitraum vor.
                              </div>
                          `
                        : ""}
                </ptc-scroller>

                <div class="padded vertical layout">
                    <ptc-month-picker
                        .max=${getRange(new Date(), "month").to}
                        @change=${({ detail: { date } }: CustomEvent<{ date: string }>) => this.go(null, { date })}
                    ></ptc-month-picker>
                </div>

                <div class="fullbleed centering layout scrim" ?hidden=${!this._loading}>
                    <ptc-ui-spinner ?active=${this._loading}></ptc-ui-spinner>
                </div>
            </div>
        `;
    }
}
